import { doRequest, doRequestWithoutAuth } from "./helpers";
import userService from "services/User";
import useAuthStore from "../stores/useAuthStore";
import {
  setRefreshToken,
  clearCredentials,
  revokeRefreshToken,
} from "./tokenStore";

export default class AuthService {
  static _storeCredentials(credentials, email = null) {
    /**
     * Store user credentials and settings in local storage.
     * Centralizes credential storage logic for all login methods.
     *
     * Note: Backend returns user_id/data but DTO marshals them as id/settings
     * Refresh token is stored separately for security isolation
     */
    useAuthStore.getState().setUserAuth({
      jwt: credentials.token,
      id: credentials.id || credentials.user_id,
      email:
        email ||
        credentials.settings?.account?.email ||
        credentials.data?.account?.email ||
        credentials.email,
      full_name: credentials.full_name,
      table_settings: credentials.settings?.preferences?.tables ||
        credentials.data?.preferences?.tables || {
          target_companies: {},
          campaigns: {},
        },
      pipeline_settings:
        credentials.settings?.preferences?.pipelines ||
        credentials.data?.preferences?.pipelines ||
        {},
      sidebar_settings:
        credentials.settings?.preferences?.sidebars ||
        credentials.data?.preferences?.sidebars ||
        {},
      role: credentials.role,
    });

    // Store refresh token separately (via tokenStore)
    if (credentials.refresh_token) {
      setRefreshToken(credentials.refresh_token);
    }

    if (
      credentials.settings?.account?.time_zone_name ||
      credentials.data?.account?.time_zone_name
    ) {
      this.setTimeZone(
        credentials.settings?.account?.time_zone_name ||
          credentials.data.account.time_zone_name
      );
    }
  }

  static async login(email, password) {
    await this.logout(); // remove current credentials first.
    const permissionCategories = await doRequest(
      "GET",
      "/role/permission_categories"
    );
    useAuthStore.getState().setPermissionCategories(permissionCategories);
    const credentials = await doRequest("POST", "/auth/login", {
      data: {
        email,
        password,
      },
    });
    this._storeCredentials(credentials, email);
  }

  static setName(name) {
    useAuthStore.getState().updateUserAuth({ full_name: name });
  }

  static setEmail(email) {
    useAuthStore.getState().updateUserAuth({ email });
  }

  static setTimeZone(timeZone) {
    useAuthStore.getState().updateUserAuth({ timeZone });
  }

  static setRole(role) {
    useAuthStore.getState().updateUserAuth({ role });
  }

  static async googleLogin(googleToken) {
    await this.logout(); // remove current credentials first
    const permissionCategories = await doRequest(
      "GET",
      "/role/permission_categories"
    );
    useAuthStore.getState().setPermissionCategories(permissionCategories);

    const credentials = await doRequest("POST", "/auth/google_login", {
      data: {
        token: googleToken,
      },
    });

    this._storeCredentials(credentials);
  }

  // Revoke refresh token on server and clear local storage
  static async logout() {
    const user = useAuthStore.getState().userAuth;

    // Update agent status to OFFLINE first (while user is still authenticated)
    if (user?.id) {
      try {
        await doRequest("PUT", `/agent_status/${user.id}`, {
          data: { status: "OFFLINE" },
        });
      } catch (e) {
        // Best-effort status update - don't fail logout if this fails
        console.warn("Failed to update user status during logout:", e);
      }
    }

    await revokeRefreshToken(doRequestWithoutAuth);
    await clearCredentials();
  }

  static isLoggedIn() {
    const { userAuth, shouldChangePassword } = useAuthStore.getState();
    return userAuth?.jwt && !shouldChangePassword;
  }

  static async sendPasswordResetEmail(email) {
    // Always returns success message to prevent user enumeration
    // Backend silently skips if user doesn't exist or is SSO-only
    let resp = await doRequest("POST", "/auth/send_reset", {
      data: {
        user_email: email,
      },
    });
    return resp;
  }

  static async validateMagicToken(token) {
    const resp = await doRequest(
      "GET",
      `/auth/validate_magic_token?token=${token}`
    );
    return resp;
  }

  static async setupPasswordWithMagicToken(token, newPassword) {
    const credentials = await doRequest("POST", "/auth/setup_password", {
      data: {
        token,
        new_password: newPassword,
      },
    });

    // Store credentials using centralized method (includes refresh token)
    const permissionCategories = await doRequest(
      "GET",
      "/role/permission_categories"
    );
    useAuthStore.getState().setPermissionCategories(permissionCategories);

    this._storeCredentials(credentials);
  }
}
